/**
 * Google Font family names — keep aligned with themes/constants.py GOOGLE_FONTS_CHOICES.
 */
export const GOOGLE_FONT_FAMILY_NAMES = [
  "Inter",
  "Roboto",
  "Open Sans",
  "Lato",
  "Montserrat",
  "Poppins",
  "Nunito",
  "Raleway",
  "Source Sans 3",
  "Noto Sans",
  "Work Sans",
  "Ubuntu",
  "PT Sans",
  "Rubik",
  "Karla",
  "DM Sans",
  "Mukta",
  "Barlow",
  "Quicksand",
  "Josefin Sans",
  "Playfair Display",
  "Merriweather",
  "Crimson Text",
  "Lora",
  "Cormorant Garamond",
  "EB Garamond",
  "Libre Baskerville",
  "Alegreya",
  "Bitter",
  "Arvo",
  "Cardo",
  "Oswald",
  "Bebas Neue",
  "Anton",
  "Righteous",
  "Cinzel",
  "Abril Fatface",
  "Pacifico",
  "Dancing Script",
  "Caveat",
  "Satisfy",
  "Great Vibes",
] as const;

/** Script/display families — request without multi-weight axis (avoids Google 400). */
const GOOGLE_FONTS_SIMPLE_CSS = new Set([
  "Bebas Neue",
  "Anton",
  "Pacifico",
  "Dancing Script",
  "Caveat",
  "Satisfy",
  "Great Vibes",
  "Abril Fatface",
  "Righteous",
]);

const COMPACT_TO_NAME = new Map<string, string>();
for (const name of GOOGLE_FONT_FAMILY_NAMES) {
  const compact = name.replace(/\s+/g, "");
  COMPACT_TO_NAME.set(compact, name);
  COMPACT_TO_NAME.set(compact.toLowerCase(), name);
}

/** Map API/mobile compact names (e.g. PlayfairDisplay) to Google CSS family names. */
export function resolveGoogleFontFamilyName(name: string | undefined | null): string {
  const raw = String(name ?? "").trim();
  if (!raw) return "";
  if (GOOGLE_FONT_FAMILY_NAMES.includes(raw as (typeof GOOGLE_FONT_FAMILY_NAMES)[number])) {
    return raw;
  }
  const compact = raw.replace(/\s+/g, "");
  const fromCompact =
    COMPACT_TO_NAME.get(compact) ?? COMPACT_TO_NAME.get(compact.toLowerCase());
  if (fromCompact) return fromCompact;
  const base = raw.replace(/[-_](Regular|Medium|SemiBold|Bold)$/i, "");
  if (base !== raw) {
    return resolveGoogleFontFamilyName(base);
  }
  return raw;
}

export function googleFontsStylesheetHref(fontFamily: string): string {
  const name = resolveGoogleFontFamilyName(fontFamily) || "Inter";
  const param = name.replace(/\s+/g, "+");
  if (GOOGLE_FONTS_SIMPLE_CSS.has(name)) {
    return `https://fonts.googleapis.com/css2?family=${param}&display=swap`;
  }
  return `https://fonts.googleapis.com/css2?family=${param}:wght@400;500;600;700&display=swap`;
}

const BRANDED_FONT_LINK_ID = "accueilv2-branded-google-font";

/** Load entreprise Google Font for web (Inter is preloaded in index.css). */
export function ensureBrandedGoogleFontLoaded(fontFamily: string | undefined | null): void {
  if (typeof document === "undefined") return;
  const resolved = resolveGoogleFontFamilyName(fontFamily);
  if (!resolved || resolved === "Inter") return;

  const href = googleFontsStylesheetHref(resolved);
  let link = document.getElementById(BRANDED_FONT_LINK_ID) as HTMLLinkElement | null;
  if (!link) {
    link = document.createElement("link");
    link.id = BRANDED_FONT_LINK_ID;
    link.rel = "stylesheet";
    document.head.appendChild(link);
  }
  if (link.href !== href) {
    link.href = href;
  }
}
